/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ObjectFactory from './ObjectFactory';
import IdGenerator from './IdGenerator';
import MetricCommonUtils from './MetricCommonUtils';
import QuerySubjectUtils from './QuerySubjectUtils';
import MetricRoleType from './MetricRoleType';
import ItemType from './ItemType';
import MoserObjectUtils from './MoserObjectUtils';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/ObjectFactory (runtime) // new
//@import com/ibm/bi/platform/moser/core/module/util/IdGenerator (static) // new
//@import com/ibm/bi/platform/moser/core/module/util/MetricCommonUtils (static) // createMetricQueryItem, setMetricDefinitionAttr
//@import com/ibm/bi/platform/moser/core/module/util/QuerySubjectUtils (static) // getParentModule
//@import com/ibm/bi/platform/moser/common/generated/metadata/MetricRoleType (static) // MetricRoleType
//@import com/ibm/bi/platform/moser/common/generated/metadata/ItemType (runtime) // new
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (runtime) // getQueryItems

/**
 * @author  Zhuoheng Huang
 */
export default class MetricUtils extends BaseMoserObject{

	/**
	 * Creates a metric definition query subject given the hierarchy structure and the data module. Generates ids for the new QS, populates it with QIs which have usage == FACT, Records the idForExprs for QIs which are time references.
	 * @param {MetricDefinitionHierarchy} metric    hierarchical structure of metric definition
	 * @param {Module} module    the data module which contains the selected QIs
	 * @param {String} label     the user defined label for the new metric definition
	 */
	static createMetricDefinitionTable(metric, module, label) {
		var objFactory = new ObjectFactory();
		var qs = objFactory.createQuerySubject();
		var items = metric.getMetricDefinitionHierarchyItemList();
		var actuals = [];
		var others = [];
		var objectFactory = new ObjectFactory();
		MetricUtils._populateMetricDefinitionTimeGrain(qs, metric);
		var idGenerator = new IdGenerator(module);
		MetricCommonUtils.setMetricDefinitionAttr(qs, label, idGenerator);
		MetricUtils._traverseHierarchy(items, actuals, others);
		idGenerator = new IdGenerator(qs);
		MetricUtils._populateMetricDefinitionQS(qs, idGenerator, objectFactory, actuals, others);
		MetricUtils._populateMetricDefinitionTimeReference(qs, metric, false);
		return qs;
	}

	/**
	 * Edit a metric definition query subject given the updated hierarchy structure. Clear the old refs and time references, record the idForExprs for latest items Update the metric definition by recursively updating QIs, and removing stale ones.
	 * @param {QuerySubject} metricDefinition   existing metric definition
	 * @param {MetricDefinitionHierarchy} hierarchy          updated metric hierarchy containing the latest QI and their attributes
	 */
	static editMetricDefinitionTable(metricDefinition, hierarchy) {
		var qs = metricDefinition.clone();
		if (qs.basicGetTimeRef()) {
			qs.basicGetTimeRef().length = 0;
		}
		if (qs.basicGetTimeGrain()) {
			qs.basicGetTimeGrain().length = 0;
		}
		var items = hierarchy.getMetricDefinitionHierarchyItemList();
		var qiMap = {};
		var idGenerator = new IdGenerator(qs);
		var objectFactory = new ObjectFactory();
		var actuals = [];
		var others = [];
		MetricUtils._traverseHierarchy(items, actuals, others);
		MetricUtils._populateMetricDefinitionTimeGrain(qs, hierarchy);
		MetricUtils._updateHierarchy(qs, qs, actuals, idGenerator, qiMap, QuerySubjectUtils.getParentModule(qs), objectFactory);
		MetricUtils._updateHierarchy(qs, qs, others, idGenerator, qiMap, QuerySubjectUtils.getParentModule(qs), objectFactory);
		MetricUtils._removeHierarchy(qs, qiMap);
		MetricUtils._populateMetricDefinitionTimeReference(qs, hierarchy, true);
		return qs;
	}

	static _populateMetricDefinitionTimeGrain(qs, metric) {
		var timeGrain = metric.getTimeGrain();
		if (timeGrain) {
			for (var __i_enFor0 = 0, __exp_enFor0 = timeGrain, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var timeG = __exp_enFor0[__i_enFor0];
				qs.addTimeGrain(timeG);
			}
		}
		qs.setTimeAnchor(metric.getTimeAnchor());
	}

	static _populateMetricDefinitionTimeReference(qs, metric, isEdit) {
		var timeReference = metric.getTimeReference();
		if (timeReference) {
			for (var __i_enFor0 = 0, __exp_enFor0 = metric.getTimeReference(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var timeRef = __exp_enFor0[__i_enFor0];
				var measures = MetricUtils._getMeasureByExpression(qs, timeRef, isEdit);
				if (measures.length==0) {
					continue;
				}
				qs.addTimeRef(measures[0].getIdentifier());
			}
		}
	}

	/**
	 * Sort the hierarchy items by types so they can be used to create new measures for different purposes
	 * @param {Array} items        all hierarchy items
	 * @param {Array} actuals      items with role ACTUAL
	 * @param {Array} others       all other items, like those with roles TIME and NONE
	 */
	static _traverseHierarchy(items, actuals, others) {
		if (!items) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			MetricUtils._traverseHierarchy(item.getChildren(), actuals, others);
			var obj = item.getRef();
			if ("QueryItem" == obj.getObjectType() || "Calculation" == obj.getObjectType()) {
				var roleType = item.getMetricRoleType();
				if (MetricRoleType.ACTUAL === roleType) {
					actuals.push(item);
				} else if (roleType) {
					others.push(item);
				}
			}
		}
	}

	/**
	 * Add the newly created measure qi as a child of the newly created clone of relatedRef the relatedRef here is from the hierarchy item, referencing the one in the data module If this block is run, it means create/edit has gone through createQueryItem for the relatedRef that means a clone of the relatedRef is already added as an item of the metric definition the idForExpr of the original relatedRef is the same as the expression of its clone use getMeasureByExpression to get its clone in the metric, and add the current qi as a child item of the clone
	 * @param {QuerySubject} metricDefinition   the metric definition QS being worked on
	 * @param {QueryItem} qi                 the newly created measure qi, this is the clone of the hierarcy item ref
	 * @param {ObjectType} relatedRef         the hierarchy item relatedRef
	 * @param {ObjectFactory} objectFactory
	 */
	static _processRelatedRef(metricDefinition, qi, relatedRef, isEdit, objectFactory) {
		if (!relatedRef) {
			return null;
		}
		if (isEdit) {
			var currentParent = MetricUtils._isValidChildParentsRoles(qi, relatedRef) ? relatedRef : metricDefinition;
			return MetricUtils._adjustMeasureHierarchy(metricDefinition, qi, currentParent, objectFactory);
		}
		var idForExpr = relatedRef.getIdForExpression();
		if (idForExpr != null) {
			var parents = MetricUtils._getMeasureByExpression(metricDefinition, idForExpr, false);
			if (!(parents.length==0)) {
				var itemType = new ItemType();
				itemType.setQueryItem(qi);
				parents[0].addItem(itemType);
			}
		}
		return null;
	}

	static _isValidChildParentsRoles(qi, parent) {
		if (!("QueryItem" == parent.getObjectType() || "QuerySubject" == parent.getObjectType())) {
			return false;
		}
		var parentQI = parent;
		var parentRole = parentQI.getMetricRole();
		if (!parentRole) {
			return true;
		}
		if (MetricRoleType.ACTUAL === parentRole && MetricRoleType.ACTUAL === qi.getMetricRole()) {
			return false;
		}
		return true;
	}

	/**
	 * Adjust the parent/child relationship of the qi measure being worked on. Check if it's parent is the same pre and post Edit If different, within the cloned metric, remove it from the original parent Get the idForExpr of current new parent from the pre-Edit metric Get the current new parent from the cloned metric given this idForExpr Add qi to this new parent
	 * @param {QuerySubject} metricDefinition   the cloned metric definition in Edit
	 * @param {QueryItem} qi                 the qi being worked on
	 * @param {ObjectType} currentParent      the new parent. In Edit, this is the item ref, either the original metric definition, or the original measure parent Note that they are both part of the pre-edit metric definition, not the cloned one
	 * @param {ObjectFactory} objectFactory
	 */
	static _adjustMeasureHierarchy(metricDefinition, qi, currentParent, objectFactory) {
		var container = qi.getParent();
		if (!container || !("ItemType" == container.getObjectType())) {
			return null;
		}
		var originalParent = container.getParent();
		var currentParentIdForExpr = currentParent.getIdForExpression();
		if (!originalParent || currentParentIdForExpr == null || currentParentIdForExpr == originalParent.getIdForExpression()) {
			return null;
		}
		if ("QueryItem" == originalParent.getObjectType()) {
			(originalParent).removeItem(container);
		} else if ("QuerySubject" == originalParent.getObjectType()) {
			(originalParent).removeItem(container);
		}
		var itemType = objectFactory.createItemType();
		itemType.setQueryItem(qi);
		var newIdForExpr;
		if (MetricRoleType.ACTUAL === qi.getMetricRole()) {
			metricDefinition.addItemAt(0, itemType);
		} else if ("QuerySubject" == currentParent.getObjectType() && currentParentIdForExpr == metricDefinition.getIdForExpression()) {
			metricDefinition.addItem(itemType);
		}
		var existingParents = MetricUtils._getMeasureByExpression(metricDefinition, currentParentIdForExpr, true);
		if (!(existingParents.length==0)) {
			var existingParent = existingParents[0];
			existingParent.addItem(itemType);
		}
		newIdForExpr = currentParent.getIdForExpression() + "." + qi.getIdentifier();
		qi.setIdForExpression(newIdForExpr);
		return newIdForExpr;
	}

	/**
	 * Adds FACT QIs and time ref QIs to the new metric definition QS, and sets its attributes.
	 * @param {QuerySubject} qs         the new metric definition QS
	 * @param  module     the data module which contains the selected QIs
	 * @param {Array} actuals      the list of hierarchy items representing QIs with usage == FACT
	 * @param  nonActuals   the list of hierarchy items representing QIs which are time references
	 */
	static _populateMetricDefinitionQS(qs, idGenerator, objectFactory, actuals, others) {
		MetricUtils._processHierarchyItem(qs, idGenerator, objectFactory, actuals);
		MetricUtils._processHierarchyItem(qs, idGenerator, objectFactory, others);
	}

	/**
	 * Sets the idForExprs of new QIs, ref and originalRef of the new QS Adds the idForExprs of time ref QIs Adds child QIs for new QIs with metricRoleType == ACTUAL
	 * @param {QuerySubject} qs       the new metric definition QS
	 * @param  module   the data module which contains the selected QIs
	 * @param {Array} items    the list of hierarchy items representing either the FACT QIs or time ref QIs
	 */
	static _processHierarchyItem(qs, idGenerator, objectFactory, items) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			MetricUtils._processSingleItem(qs, idGenerator, objectFactory, item, qs.getItem().length);
		}
	}

	static _processSingleItem(qs, idGenerator, objectFactory, item, index) {
		var qi = MetricCommonUtils.createMetricQueryItem(idGenerator, item.getRef(), item.getMetricRoleType(), item.getMetricSentiment(), objectFactory);
		if (qi) {
			MetricUtils._processRelatedRef(qs, qi, item.getRelatedRef(), false, objectFactory);
		}
		if (!item.getRelatedRef()) {
			var itemType = objectFactory.createItemType();
			itemType.setQueryItem(qi);
			qs.addItemAt(index, itemType);
		}
		return qi;
	}

	/**
	 * For each measure QI item in the updated hierarchy, if the QI already exists, update the role and timeRef in parent metric, if needed If the QI doesn't exist yet, add it to the metric definition QS Record a mapping from the idForExpr to the processed QIs. The map is used for removing stale QIs.
	 * @param {QuerySubject} qs             the metric definition QS being updated
	 * @param {ObjectType} currentParent  the parent of the current item(s) being worked on
	 * @param {Array} items          the list of hierarchy items representing the updated items
	 * @param {IdGenerator} idGenerator    the idGenerator
	 * @param {Map} qiMap          A map with the keys being idForExprs and values being processed QIs
	 * @param {Module} module         the data module where the original metric definition comes from
	 * @param {ObjectFactory} objectFactory
	 */
	static _updateHierarchy(qs, currentParent, items, idGenerator, qiMap, module, objectFactory) {
		if (!items) {
			return;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var parent = currentParent;
			var obj = item.getRef();
			var role = item.getMetricRoleType();
			if (!("QueryItem" == obj.getObjectType()) && !("Calculation" == obj.getObjectType()) || !role) {
				continue;
			}
			var baseQueryitemType = obj;
			var idForExpr = baseQueryitemType.getIdForExpression();
			var existingQIs = MetricUtils._getMeasureByExpression(qs, idForExpr, true);
			if (!(existingQIs.length==0)) {
				var existingQI = existingQIs[0];
				existingQI.setMetricRole(role);
				if (MetricRoleType.ACTUAL == role) {
					parent = qs;
				}
				var relatedRef = item.getRelatedRef();
				var newIdForExpr = null;
				if (relatedRef) {
					newIdForExpr = MetricUtils._processRelatedRef(qs, existingQI, relatedRef, true, objectFactory);
				} else {
					newIdForExpr = MetricUtils._adjustMeasureHierarchy(qs, existingQI, parent, objectFactory);
				}
				if (newIdForExpr != null) {
					obj.setIdForExpression(newIdForExpr);
				}
				qiMap[existingQI.getIdForExpression()] = existingQI;
				continue;
			}
			var index = MetricRoleType.ACTUAL === role ? 0 : qs.getItem().length;
			var qi = MetricUtils._processSingleItem(qs, idGenerator, objectFactory, item, index);
			if (!qi) {
				continue;
			}
			idForExpr = qi.getIdForExpression();
			if (idForExpr != null) {
				qiMap[qi.getIdForExpression()] = qi;
			}
		}
	}

	static _removeHierarchy(obj, qiMap) {
		var currentItems;
		switch (obj.getObjectType()) {
		case "QuerySubject":
			currentItems = (obj).basicGetItem();
			break;
		case "QueryItem":
			currentItems = (obj).basicGetItem();
			break;
		case "Folder":
			currentItems = (obj).basicGetItem();
			break;
		default:
			break;
		}
		if (!currentItems) {
			return;
		}
		for (var i = currentItems.length - 1; i >= 0; --i) {
			var item = currentItems[i];
			var folder = item.getFolder();
			if (folder) {
				MetricUtils._removeHierarchy(folder, qiMap);
			}
			var qi = item.getQueryItem();
			if (qi) {
				var exp = qi.getIdForExpression();
				if (!qiMap[exp]) {
					MetricUtils._removeHierarchyItem(obj, item);
				} else {
					MetricUtils._removeHierarchy(qi, qiMap);
				}
			}
		}
	}

	static _removeHierarchyItem(obj, item) {
		switch (obj.getObjectType()) {
		case "QuerySubject":
			(obj).removeItem(item);
			break;
		case "QueryItem":
			(obj).removeItem(item);
			break;
		case "Folder":
			(obj).removeItem(item);
			break;
		default:
			break;
		}
	}

	/**
	 * Given the idForExpr from the updated measure QI in the hierarchy, iterate through the metric QS and return the QI when its expression == idForExpr passed in The expressions of QIs under a metric QS are unique, because they are the same as the idForExprs of original module QIs upon creation
	 * @param {QuerySubject} qs          the metric definition QS being updated
	 * @param {String} idForExpr   the idForExpr of updated measure QI in the hierarchy item
	 */
	static _getMeasureByExpression(qs, idForExpr, byIdForExpr) {
		var res = [];
		if (!qs || idForExpr == null) {
			return res;
		}
		var items = MoserObjectUtils.getQueryItems(qs.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			var exp = byIdForExpr ? qi.getIdForExpression() : qi.getExpression();
			if (exp == null) {
				continue;
			}
			if (exp == idForExpr) {
				res.push(qi);
			}
		}
		return res;
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/MetricUtils.js